import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMuvelet } from '../felhasznalo-context.ts';

import {
  api,
  type JaratNap,
  type JaratRendezes,
  type JaratSor,
  type NapAdat,
  type NapokValasz,
} from '../api.ts';
import { useAlapadatok } from '../alapadatok-context.ts';
import { ft, szam } from '../formazas.ts';
import { letolt } from '../letoltes.ts';
import { EtteremKereso } from '../reszek/EtteremKereso.tsx';
import { FpluszJelolo } from '../reszek/FpluszJelolo.tsx';
import { JaratlapImport } from '../reszek/JaratlapImport.tsx';
import { Mezo, SzamMezo, ValasztoMezo } from '../reszek/Mezo.tsx';
import { TuraNezet } from '../reszek/TuraNezet.tsx';

const RENDEZES_SORREND: JaratRendezes[] = ['felvetel', 'etterem', 'irsz', 'cim'];

/** A két nézet: az összeírás és a sofőrönkénti túrasorrend. (2026-10-22) */
type JaratNezet = 'osszeiras' | 'soforonkent';

/**
 * Járatszervezés — a napi lap összeállítása. (Megbízás 6.1)
 *
 * Reggel itt áll össze a nap, majd kinyomtatva megy a sofőrökkel. Amit itt
 * beírunk, az a **terv**; a tényleges mennyiségeket másnap, az Ellenőrzés
 * képernyőn rögzítjük.
 *
 * A járatlapon szándékosan **nincs Összeg oszlop** — csak mennyiség és ár.
 * Az összeget a sofőr írja a nyomtatott lapra.
 */
export function Jaratszervezes({
  onNyomtatas,
  kezdoNap,
}: {
  onNyomtatas: (datum: string) => void;
  /** Ha a Járatlapok listájából léptünk ide, ezt a napot nyitjuk meg. */
  kezdoNap?: string | null;
}) {
  const alapadatok = useAlapadatok();

  /** Letöltés csak engedéllyel. (3.0 / 1.) */
  const letolthet = useMuvelet('export_jarat');
  /** Kiadott lap visszanyitása tervezésre — iroda, diszpécser, admin. */
  const visszanyithatJog = useMuvelet('jarat_visszanyit');
  const torolhetJog = useMuvelet('jarat_torles');
  const visszanyithat = visszanyithatJog || torolhetJog;
  const [napok, setNapok] = useState<NapAdat[]>([]);
  const [statuszNevek, setStatuszNevek] = useState<Record<string, string>>({});
  const [rendezesNevek, setRendezesNevek] = useState<Record<string, string>>({});
  const [datum, setDatum] = useState<string | null>(kezdoNap ?? null);
  const [jarat, setJarat] = useState<JaratNap | null>(null);
  const [toltes, setToltes] = useState(true);
  const [hiba, setHiba] = useState<string | null>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);
  const [aktivRendezes, setAktivRendezes] = useState<JaratRendezes>('felvetel');
  const [importNyitva, setImportNyitva] = useState(false);
  /** Összeírás vagy sofőrönkénti túrasorrend. (2026-10-22) */
  const [nezet, setNezet] = useState<JaratNezet>('osszeiras');

  /* --- napok betöltése --- */
  useEffect(() => {
    api
      .get<NapokValasz>('/api/jarat/napok')
      .then((v) => {
        setNapok(v.napok);
        setStatuszNevek(v.statusz_nevek);
        setRendezesNevek(v.rendezes_nevek);
        // Az első olyan nappal indulunk, ami még tervezés alatt van;
        // ha nincs ilyen, az elsővel. (Ha a Járatlapokból jöttünk, az
        // ottani nap már be van állítva.)
        // (A napok listája a már rögzített napokat is tartalmazza, ezért a
        // Járatlapokból érkező korábbi nap is szerepel benne.)
        const elso = v.napok.find((n) => n.statusz === 'tervezes') ?? v.napok[0];
        setDatum((d) => d ?? elso?.datum ?? null);
      })
      .catch((h: Error) => setHiba(h.message));
  }, []);

  const betolt = useCallback(async () => {
    if (!datum) return;
    setToltes(true);
    try {
      setJarat(await api.get<JaratNap>(`/api/jarat/${datum}`));
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni a napot.');
    } finally {
      setToltes(false);
    }
  }, [datum]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  /** A napok listájának frissítése — a címszámok miatt. */
  const napokFrissit = useCallback(async () => {
    try {
      const v = await api.get<NapokValasz>('/api/jarat/napok');
      setNapok(v.napok);
    } catch {
      /* a lista frissítése nem létfontosságú */
    }
  }, []);

  const ment = (id: number, mezo: string) => async (ertek: string) => {
    const frissitett = await api.patch<JaratSor>(`/api/jarat/tetel/${id}/mezo/${mezo}`, {
      ertek,
    });
    setJarat((elozo) =>
      elozo
        ? { ...elozo, sorok: elozo.sorok.map((s) => (s.id === id ? frissitett : s)) }
        : elozo,
    );
    // Az összesítés és a figyelmeztetések a szerveren számolódnak.
    void betolt();
  };

  async function muvelet<T>(mit: () => Promise<T>, sikerUzenet?: (e: T) => string) {
    setHiba(null);
    setUzenet(null);
    try {
      const eredmeny = await mit();
      if (sikerUzenet) setUzenet(sikerUzenet(eredmeny));
      await betolt();
      await napokFrissit();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A művelet nem sikerült.');
    }
  }

  const fizetesiLehetosegek = useMemo(
    () =>
      alapadatok.fizetesi_modok
        .filter((m) => m.kod !== 'KP')
        .map((m) => ({ ertek: m.kod, cimke: m.nev })),
    [alapadatok.fizetesi_modok],
  );

  if (!datum) {
    return <div className="halvany" style={{ padding: 20 }}>Betöltés…</div>;
  }

  const o = jarat?.osszesites;

  /** Az egyéb címek mentése — a mezőből kilépve, mint a többi mező. */
  async function egyebCimekMentese(szoveg: string) {
    if ((jarat?.nap.egyeb_cimek ?? '') === szoveg.trim()) return;
    try {
      setJarat(await api.post<JaratNap>(`/api/jarat/${datum}/egyeb-cimek`, { szoveg }));
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Az egyéb címeket nem sikerült menteni.');
    }
  }
  const tervezheto = jarat?.tervezheto ?? false;
  const statusz = jarat?.nap.statusz ?? 'tervezes';
  const soforok = jarat?.soforok ?? [];
  /** Hány címnek van már túrasorrendje — a fül csipjén ez a szám áll. */
  const turazhatoDb = (jarat?.sorok ?? []).filter((s) => s.tura_sorrend != null).length;

  return (
    <>
      <div className="dayhead">
        <select value={datum} onChange={(e) => setDatum(e.target.value)}>
          {napok.map((n) => (
            <option key={n.datum} value={n.datum}>
              {n.felirat}
              {n.cimek > 0 ? ` · ${n.cimek} cím` : ''}
            </option>
          ))}
        </select>

        <span className={`allapotjel ${statusz}`}>{statuszNevek[statusz] ?? statusz}</span>

        <span className="hetjel">
          <b>{jarat?.nap.het}</b>. hét · {jarat?.nap.het != null && jarat.nap.het % 2 === 0 ? 'páros' : 'páratlan'}
        </span>

        <div className="stats">
          <div>
            Cím<strong>{o?.cimek ?? 0}</strong>
          </div>
          <div>
            Liter<strong>{ft(o?.liter_osszes ?? 0)}</strong>
          </div>
          <div>
            MO<strong>{ft(o?.mo ?? 0)}</strong>
          </div>
          <div>
            FO<strong>{ft(o?.fo ?? 0)}</strong>
          </div>
          <div>
            HO<strong>{ft(o?.hordo ?? 0)}</strong>
          </div>
          <div>
            Raktári<strong>{ft(o?.raktari ?? 0)}</strong>
          </div>
        </div>
      </div>

      <div className="actbar">
        <button
          className="btn gold"
          disabled={!tervezheto || (jarat?.behuzhato ?? 0) === 0}
          onClick={() =>
            void muvelet(
              () => api.post<{ felvett: number; kihagyott: number }>(`/api/jarat/${datum}/allandok`),
              (e) =>
                e.felvett === 0
                  ? 'Minden állandó cím már szerepel a lapon.'
                  : `${e.felvett} állandó cím behúzva${e.kihagyott ? `, ${e.kihagyott} már bent volt` : ''}.`,
            )
          }
        >
          Állandók behúzása
        </button>

        <button
          className="btn sm"
          disabled={statusz !== 'tervezes' || (o?.cimek ?? 0) === 0}
          title={
            statusz !== 'tervezes'
              ? 'Csak tervezés alatt — a kiadott lapon szereplő ár már a sofőrnél van.'
              : undefined
          }
          onClick={() =>
            void muvelet(
              () => api.post<{ frissitett: number }>(`/api/jarat/${datum}/arak`),
              (e) =>
                e.frissitett === 0
                  ? 'Minden ár egyezik a törzzsel.'
                  : `${e.frissitett} sor ára frissítve a törzsből.`,
            )
          }
        >
          Árak frissítése a törzsből
        </button>

        <button
          className={`btn sm${importNyitva ? ' pri' : ''}`}
          disabled={statusz === 'lezarva'}
          title="Excelben vagy CSV-ben összeállított járatlap beolvasása"
          onClick={() => setImportNyitva((v) => !v)}
        >
          {importNyitva ? '× Import bezárása' : 'Járatlap importálása'}
        </button>

        <span className="hint">
          {(jarat?.behuzhato ?? 0) > 0
            ? `${jarat!.behuzhato} állandó cím van erre a napra rögzítve.`
            : 'Erre a napra nincs behúzható állandó cím.'}
        </span>
      </div>

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}
      {uzenet && (
        <div className="uzenet ok" onClick={() => setUzenet(null)}>
          {uzenet}
        </div>
      )}

      {importNyitva && (
        <JaratlapImport
          datum={datum}
          onKesz={(frissJarat, szoveg) => {
            setImportNyitva(false);
            setJarat(frissJarat);
            setUzenet(szoveg);
            void napokFrissit();
          }}
          onMegse={() => setImportNyitva(false)}
        />
      )}

      {/*
        Két nézet. (A megrendelő kérése, 2026-10-22.)

        Az **Összeírás** a mostani képernyő, változatlanul: itt áll össze a
        nap és itt történik a kiosztás. A **Sofőrönként** nézet utána jön:
        ott már csak az számít, ki milyen sorrendben járja végig a saját
        címeit. Külön nézet, mert a tizenöt oszlopos tábla mellett a
        sorrendezés elveszne.
      */}
      <div className="fchips" style={{ marginTop: 12 }}>
        {(
          [
            ['osszeiras', 'Összeírás'],
            ['soforonkent', 'Sofőrönként'],
          ] as Array<[JaratNezet, string]>
        ).map(([k, cimke]) => (
          <button
            key={k}
            className={`fchip${nezet === k ? ' on' : ''}`}
            onClick={() => setNezet(k)}
          >
            {cimke}
            {k === 'soforonkent' && <b>{turazhatoDb}</b>}
          </button>
        ))}
        {nezet === 'soforonkent' && (
          <span className="hint">
            A sorrend csak tervezés alatt állítható, és nem öröklődik a következő napra.
          </span>
        )}
      </div>

      {nezet === 'soforonkent' ? (
        <TuraNezet
          datum={datum}
          jarat={jarat}
          soforok={soforok}
          tervezheto={tervezheto}
          onHiba={setHiba}
          onUzenet={setUzenet}
          onFrissit={betolt}
        />
      ) : (
      <div className="split" style={{ marginTop: 14 }}>
        <EtteremKereso
          fejlec="Étterem hozzáadása"
          fizetesiLehetosegek={fizetesiLehetosegek}
          gombCimke="Hozzáadás"
          gombStilus="pri"
          bovitett
          onValaszt={async (e, ertekek) => {
            await api.post(`/api/jarat/${datum}/tetel`, {
              etterem_id: e.id,
              terv_mo: ertekek.mo,
              terv_fo: ertekek.fo,
              terv_hordo: ertekek.hordo,
              terv_liter: ertekek.mennyiseg,
              fplusz: ertekek.fplusz,
              fizetesi_mod: ertekek.fizetesi_mod || null,
            });
            await betolt();
            await napokFrissit();
          }}
        />

        <div>
          {/*
            Kiosztó sáv. (2.0 / 5.2)
            Automatikus elosztás szándékosan nincs — a címeket az iroda
            osztja szét, mert tudja, ki merre megy és mibe fér bele.
          */}
          {(o?.cimek ?? 0) > 0 && (
            <div className="kiosztosav">
              <span className="lbl" style={{ margin: 0 }}>
                Kiosztás
              </span>
              {(jarat?.kiosztas ?? []).map((k) => (
                <span
                  key={k.sofor_id ?? 'nincs'}
                  className={`kiosztochip${k.sofor_id === null && k.cimek > 0 ? ' gond' : ''}`}
                >
                  {k.nev}
                  <b>{k.cimek}</b>
                  <em>{ft(k.liter)} l</em>
                </span>
              ))}
              <button
                className="btn sm"
                disabled={
                  !tervezheto ||
                  (jarat?.kiosztas ?? []).every((k) => k.sofor_id === null || k.cimek === 0)
                }
                title="Csak a tervezéskori kiosztást szedi le; amit már kitöltöttek, az marad."
                onClick={() =>
                  void muvelet(
                    () => api.post<{ torolt: number }>(`/api/jarat/${datum}/kiosztas-torles`),
                    (e) => `${e.torolt} cím kiosztása törölve.`,
                  )
                }
              >
                Kiosztás törlése
              </button>
            </div>
          )}

          <div className="sortbar">
            <span>Rendezés</span>
            {RENDEZES_SORREND.map((r) => (
              <button
                key={r}
                className={`sb${aktivRendezes === r ? ' on' : ''}`}
                disabled={!tervezheto || (o?.cimek ?? 0) === 0}
                onClick={() => {
                  setAktivRendezes(r);
                  void muvelet(() =>
                    api.post<JaratNap>(`/api/jarat/${datum}/rendezes`, { mod: r }),
                  );
                }}
              >
                {rendezesNevek[r] ?? r}
              </button>
            ))}
            <span className="hint">
              {aktivRendezes === 'irsz' || aktivRendezes === 'cim'
                ? 'A sorrend a nyomtatott lapon is ez lesz.'
                : ''}
            </span>
          </div>

          <div className="sheet">
            <table>
              <thead>
                <tr>
                  <th className="n">#</th>
                  <th className="n kan">
                    K.
                    <br />
                    vis
                  </th>
                  <th className="n kan">
                    K.
                    <br />
                    ki
                  </th>
                  <th>Étterem</th>
                  <th className="n g">MO</th>
                  <th className="n">FO</th>
                  <th className="n" title="Kihelyezendő hordó">
                    HO
                  </th>
                  <th className="n g">Mennyiség</th>
                  <th className="n">Ár</th>
                  <th className="g">Fizetés</th>
                  <th style={{ width: 110 }}>Sofőr</th>
                  <th>Megjegyzés</th>
                  <th>Cím</th>
                  <th className="g">Áll.</th>
                </tr>
              </thead>
              <tbody>
                {(jarat?.sorok ?? []).map((s, i) => (
                  <JaratSorNezet
                    key={s.id}
                    s={s}
                    sorszam={i + 1}
                    tervezheto={tervezheto}
                    fizetesiLehetosegek={fizetesiLehetosegek}
                    soforok={jarat?.soforok ?? []}
                    onMent={ment}
                    onCsillag={() =>
                      void muvelet(() => api.post(`/api/jarat/tetel/${s.id}/allando`))
                    }
                    onTorol={() =>
                      void muvelet(() => api.torol(`/api/jarat/tetel/${s.id}`))
                    }
                  />
                ))}
              </tbody>
              {(o?.cimek ?? 0) > 0 && (
                <tfoot>
                  <tr>
                    <td></td>
                    <td className="lab" colSpan={3}>
                      Összesen
                    </td>
                    <td className="g">{szam(o!.mo, 0)}</td>
                    <td>{szam(o!.fo, 0)}</td>
                    <td>{szam(o!.hordo, 0)}</td>
                    <td className="g">{szam(o!.liter, 0)}</td>
                    <td></td>
                    <td className="g"></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    {/* Az „Áll." oszlop — a fejléc 14 oszlopos. */}
                    <td className="g"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {(o?.cimek ?? 0) === 0 && (
            <div className="blank">
              {toltes
                ? 'Betöltés…'
                : 'Ez a nap üres. Húzd be az állandókat, vagy adj hozzá éttermet a keresőből.'}
            </div>
          )}

          {/*
            * Egyéb címek: útközbeni bolt, benzinkút, technikai cím — ami nem
            * partner. Rákerül a nyomtatott járatlapra, hogy ne kézzel kelljen
            * ráírni; máshova nem kerül belőle adat.
            * (A megrendelő kérése, 2026-09-28.)
            */}
          <div className="panel egyebdoboz" style={{ marginTop: 12 }}>
            <p className="lbl">Egyéb címek — ami nem partner</p>
            <div className="hint" style={{ margin: '-4px 0 8px' }}>
              Útközbeni bolt, benzinkút, technikai cím. A járatlap aljára nyomtatjuk, hogy ne
              kézzel kelljen ráírni. Partnert nem veszünk fel belőle, és kimutatásba sem kerül.
            </div>
            <textarea
              rows={3}
              defaultValue={jarat?.nap.egyeb_cimek ?? ''}
              placeholder="soronként egy — pl. Metro Budaörs · benzinkút M1 · Praktiker, festék"
              disabled={!tervezheto}
              onBlur={(e) => void egyebCimekMentese(e.target.value)}
            />
          </div>

          <div className="warns">
            {(jarat?.figyelmeztetesek ?? []).map((f) => (
              <div key={f.fajta} className="warn">
                {f.szoveg}
              </div>
            ))}
          </div>

          <div className="actbar">
            <button
              className="btn"
              disabled={(o?.cimek ?? 0) === 0}
              onClick={() => onNyomtatas(datum)}
            >
              Nyomtatási kép
            </button>
            {letolthet && (
              <button
                className="btn sm"
                disabled={(o?.cimek ?? 0) === 0}
                title="A nyomtatott lap szerkezetével. Ha van kiosztás, sofőrönként külön munkalapon is."
                onClick={() => letolt(`/api/export/jaratlap/${datum}`)}
              >
                Excel
              </button>
            )}
            <button
              className="btn pri"
              disabled={statusz !== 'tervezes' || (o?.cimek ?? 0) === 0}
              onClick={() =>
                void muvelet(
                  () => api.post(`/api/jarat/${datum}/statusz`, { statusz: 'kiadva' }),
                  () => 'A nap kiadva. Innentől az Ellenőrzés képernyőn folytatódik.',
                )
              }
            >
              {statusz === 'tervezes' ? 'Járatlap kiadása' : 'Kiadva ✓'}
            </button>
            {/*
              Visszaút a tervezésre. (A megrendelő kérése, 2026-10-26.)

              Eddig a kiadás egyirányú utca volt a képernyőn: ha menet
              közben derült ki, hogy még igazítani kell a lapon, nem lehetett
              visszalépni. Lezárt sofőr mellett a szerver így sem engedi —
              olyankor előbb az Ellenőrzésen kell visszanyitni.
            */}
            {(statusz === 'kiadva' || statusz === 'ellenorzes') && visszanyithat && (
              <button
                className="btn sm"
                title="A nap visszakerül tervezésre, hogy a lapon igazítani lehessen."
                onClick={() => {
                  const igen = window.confirm(
                    `Biztosan visszanyitod a(z) ${datum} járatlapját tervezésre?\n\n` +
                      'A sofőröknél lévő papír ettől nem változik meg — nyomtasd ki újra, ' +
                      'ha módosítasz rajta. A beírt tényleges mennyiségek megmaradnak.',
                  );
                  if (!igen) return;
                  void muvelet(
                    () => api.post(`/api/jarat/${datum}/visszanyitas`),
                    () => 'A járatlap újra tervezés alatt van.',
                  );
                }}
              >
                Járatlap újranyitása
              </button>
            )}
            <span className="hint">
              {statusz === 'tervezes'
                ? 'Kiadás után a nap átkerül az Ellenőrzés fülre.'
                : statusz === 'ellenorzes'
                  ? 'Ez a nap már ellenőrzés alatt van. Újranyitva ismét szerkeszthető a terv.'
                  : 'Ez a nap már ki van adva. Utólagos cím itt még felvehető.'}
            </span>
          </div>
        </div>
      </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

function JaratSorNezet({
  s,
  sorszam,
  tervezheto,
  fizetesiLehetosegek,
  soforok,
  onMent,
  onCsillag,
  onTorol,
}: {
  s: JaratSor;
  sorszam: number;
  tervezheto: boolean;
  fizetesiLehetosegek: Array<{ ertek: string; cimke: string }>;
  /** A választható sofőrök a kiosztáshoz. (2.0) */
  soforok: Array<{ id: number; nev: string }>;
  onMent: (id: number, mezo: string) => (ertek: string) => Promise<void>;
  onCsillag: () => void;
  onTorol: () => void;
}) {
  const ment = (mezo: string) => onMent(s.id, mezo);
  /** Címet kivenni a napból külön engedély. (3.0 / 1., 2026-09-27) */
  const torolhet = useMuvelet('jarat_torles');

  // A fizetési mód színnel is látszik: átutalás kék, raktári arany —
  // a nyomtatott lapon ezek a kivételek, a készpénz az alapeset.
  const fizetesOsztaly =
    s.fizetesi_mod === 'Á' ? 'p tr' : s.fizetesi_mod === 'R' ? 'p rr' : 'p';

  return (
    <tr className={s.utolag ? 'late' : undefined}>
      <td className="idx">{sorszam}</td>
      {/* Ezt a két oszlopot a sofőr tölti ki kézzel a papíron. */}
      <td className="hand"></td>
      <td className="hand"></td>
      <td className="nm">
        <b>
          {s.etterem_nev}
          {/* Kihelyezendő hordó: sárga címke, a nyomtatott lapon is. */}
          {(s.terv_hordo ?? 0) > 0 && (
            <span className="hojel">+{szam(s.terv_hordo ?? 0, 0)} HO</span>
          )}
          {/* Az F+ jelölő a név mellett: kattintásra átáll, az ár vele vált. */}
          <FpluszJelolo
            ertek={s.fplusz}
            vanAra={s.van_fplusz}
            tiltva={!tervezheto}
            onMent={ment('fplusz')}
          />
          {/* Egyedi moslékár, ha eltér az alapártól. */}
          {s.mo_egyedi && (
            <span className="mojel" title="Egyedi moslékár — eltér az alapártól">
              MO {ft(s.mo_egyedi)}
            </span>
          )}
        </b>
      </td>
      <td className="q g">
        <SzamMezo ertek={s.terv_mo} onMent={ment('terv_mo')} helykitolto="0" osztaly="c" tiltva={!tervezheto} />
      </td>
      <td className="q">
        <SzamMezo ertek={s.terv_fo} onMent={ment('terv_fo')} helykitolto="0" osztaly="c" tiltva={!tervezheto} />
      </td>
      <td className="q">
        <SzamMezo
          ertek={s.terv_hordo}
          onMent={ment('terv_hordo')}
          helykitolto="0"
          osztaly="c"
          tiltva={!tervezheto}
        />
      </td>
      <td className="q g">
        <Mezo
          ertek={s.terv_liter_x ? 'x' : s.terv_liter}
          onMent={ment('terv_liter')}
          helykitolto="0"
          osztaly="c"
          tiltva={!tervezheto}
          cim='Szám, vagy "x", ha a mennyiséget telefonon egyeztetitek.'
        />
      </td>
      <td className="q">
        <SzamMezo ertek={s.egysegar} onMent={ment('egysegar')} osztaly="c" tiltva={!tervezheto} />
      </td>
      <td className="g">
        <ValasztoMezo
          ertek={s.fizetesi_mod}
          uresCimke="Készpénz"
          lehetosegek={fizetesiLehetosegek}
          onMent={ment('fizetesi_mod')}
          osztaly={fizetesOsztaly}
        />
      </td>
      <td>
        <ValasztoMezo
          ertek={s.sofor_id === null ? null : String(s.sofor_id)}
          lehetosegek={soforok.map((so) => ({ ertek: String(so.id), cimke: so.nev }))}
          uresCimke="— nincs —"
          osztaly={s.sofor_id === null ? 'nincs-sofor' : undefined}
          tiltva={!tervezheto}
          onMent={ment('sofor_id')}
        />
      </td>
      <td className="note">
        <Mezo
          ertek={s.megjegyzes}
          onMent={ment('megjegyzes')}
          osztaly="c megjnote"
          tiltva={!tervezheto}
        />
      </td>
      <td className="addr">
        {[s.etterem_irsz, s.etterem_cim].filter(Boolean).join(' ') || '—'}
      </td>
      <td className="act g">
        <button
          className={`star${s.allando ? ' on' : ''}`}
          title={
            s.allando
              ? 'Ez a cím állandó ezen a hétköznapon — kattints a levételhez'
              : 'Állandóvá tétel ezen a hétköznapon'
          }
          onClick={onCsillag}
        >
          {s.allando ? '★' : '☆'}
        </button>
        {torolhet && (
          <button className="xb" title="Sor törlése" disabled={!tervezheto} onClick={onTorol}>
            ×
          </button>
        )}
      </td>
    </tr>
  );
}
